import React from 'react';
import {Avatar, Body, Button} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {Brand} from '@/types/brand';
import styles from './BrandListItem.module.scss';

interface BrandListItemProps {
  brand: Brand;
  onConfigure: () => void;
}

function getConnectedAccounts(brand: Brand) {
  const accounts: {name: string; label: string}[] = [];

  const igAccount = brand.brandInstagramBusinessAccount;
  if (igAccount && igAccount.state === 'active') {
    accounts.push({name: 'instagram', label: 'Instagram'});
  }

  if (brand.hasShopifyAccessToken) {
    accounts.push({name: 'shopify', label: 'Shopify'});
  }

  const tiktokAuth = brand.tiktokBusinessAuthorizations?.[0];
  if (
    tiktokAuth &&
    (tiktokAuth.authorizationState === 'pending' ||
      tiktokAuth.authorizationState === 'success')
  ) {
    accounts.push({name: 'tiktok', label: 'TikTok'});
  }

  if (brand.walmartConnectAdvertiserName) {
    accounts.push({name: 'walmart', label: 'Walmart Connect'});
  }

  return accounts;
}

function BrandListItem({brand, onConfigure}: BrandListItemProps) {
  const connectedAccounts = getConnectedAccounts(brand);
  const avatarUrl = brand.displayAvatarUrl?.includes('placeholder_avatar')
    ? null
    : brand.displayAvatarUrl;

  return (
    <div className={styles.container} data-test-brand-list-item={brand.id}>
      <div className={styles.brandInfo}>
        <span data-test-brand-editable-avatar={brand.id}>
          <Avatar
            size="lg"
            type={avatarUrl ? 'picture' : 'initials'}
            src={avatarUrl || undefined}
            initials={brand.displayName?.charAt(0)?.toUpperCase() || '?'}
          />
        </span>
        <Body
          size="md"
          className={styles.brandName}
          data-test-brand-list-item-name={brand.id}
        >
          {brand.displayName}
        </Body>
      </div>

      <div className={styles.connectedAccounts}>
        {connectedAccounts.length > 0 ? (
          <div className={styles.accountsList}>
            {connectedAccounts.map((account) => (
              <div
                key={account.name}
                className={styles.accountBadge}
                data-test-brand-list-item-connected-account={account.name}
              >
                <span>
                  {translate('{accountName} connected', {
                    accountName: account.label,
                  })}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div
            className={styles.emptyAccounts}
            data-test-brand-list-item-connected-accounts-empty={brand.id}
          >
            <span className={styles.emptyLabel}>
              {translate('No connected accounts')}
            </span>
            <span className={styles.emptyHint}>
              {translate('Instagram · Shopify · TikTok · Walmart Connect')}
            </span>
          </div>
        )}

        <Button
          appearance="neutral"
          mode="plain"
          size="small"
          onClick={onConfigure}
          data-test-brand-list-item-update-brand={brand.id}
        >
          {translate('Configure')}
        </Button>
      </div>
    </div>
  );
}

export default BrandListItem;
